import React, { useState } from 'react';
import {
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  InputNumber,
  Button,
  Space,
  Alert,
  Descriptions,
  Tag,
} from 'antd';
import {
  DollarOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import { createPaymentRequest } from '../../redux/actions/paymentActions';

const { Option } = Select;

const PAYMENT_MODES = [
  { value: 'neft',   label: 'NEFT',   hint: 'UTR Number' },
  { value: 'rtgs',   label: 'RTGS',   hint: 'UTR Number' },
  { value: 'cheque', label: 'Cheque', hint: 'Cheque Number' },
  { value: 'upi',    label: 'UPI',    hint: 'Transaction ID' },
  { value: 'cash',   label: 'Cash',   hint: null },
  { value: 'dd',     label: 'DD',     hint: 'DD Number' },
];

const fmtMoney = (val) =>
  val != null
    ? `₹ ${Number(val).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`
    : '—';

/**
 * RecordPaymentModal
 *
 * Props:
 *  open          — boolean
 *  onClose       — callback
 *  bill          — KpBillReceipt object (must have id, bill_number, bill_amount)
 *  paymentSummary — PaymentSummary object (optional; used to show outstanding)
 */
const RecordPaymentModal = ({ open, onClose, bill, paymentSummary }) => {
  const dispatch = useDispatch();
  const { actionLoading } = useSelector((s) => s.payments);

  const [form] = Form.useForm();
  const [selectedMode, setSelectedMode] = useState('neft');

  const outstanding = paymentSummary?.outstanding ?? bill?.bill_amount ?? 0;
  const modeConfig = PAYMENT_MODES.find((m) => m.value === selectedMode);
  const needsRef = selectedMode !== 'cash';

  const handleSubmit = () => {
    form.validateFields().then((values) => {
      dispatch(
        createPaymentRequest({
          bill_receipt_id: bill.id,
          payment_date: values.payment_date.format('YYYY-MM-DD'),
          payment_mode: values.payment_mode,
          reference_number: values.reference_number || '',
          amount: values.amount,
          status: values.status || 'completed',
          remarks: values.remarks || '',
        })
      );
      form.resetFields();
      onClose();
    });
  };

  const handleCancel = () => {
    form.resetFields();
    setSelectedMode('neft');
    onClose();
  };

  return (
    <Modal
      title={
        <Space>
          <DollarOutlined style={{ color: '#1890ff' }} />
          Record Payment
        </Space>
      }
      open={open}
      onCancel={handleCancel}
      footer={
        <Space>
          <Button onClick={handleCancel}>Cancel</Button>
          <Button
            type="primary"
            icon={<DollarOutlined />}
            loading={actionLoading}
            onClick={handleSubmit}
          >
            Record Payment
          </Button>
        </Space>
      }
      width={560}
      destroyOnClose
    >
      {/* Bill context */}
      <Descriptions size="small" bordered column={2} style={{ marginBottom: 16 }}>
        <Descriptions.Item label="Bill">
          <strong>{bill?.bill_number || `#${bill?.id}`}</strong>
        </Descriptions.Item>
        <Descriptions.Item label="Bill Amount">
          <strong style={{ color: '#1890ff' }}>{fmtMoney(bill?.bill_amount)}</strong>
        </Descriptions.Item>
        {paymentSummary && (
          <>
            <Descriptions.Item label="Total Paid">
              <span style={{ color: '#52c41a' }}>{fmtMoney(paymentSummary.total_paid)}</span>
            </Descriptions.Item>
            <Descriptions.Item label="Outstanding">
              <strong style={{ color: outstanding > 0 ? '#fa8c16' : '#52c41a' }}>
                {fmtMoney(outstanding)}
              </strong>
            </Descriptions.Item>
          </>
        )}
      </Descriptions>

      {outstanding <= 0 && (
        <Alert
          type="success"
          showIcon
          message="This bill is fully paid. No outstanding amount remaining."
          style={{ marginBottom: 16 }}
        />
      )}

      <Form
        form={form}
        layout="vertical"
        initialValues={{
          payment_date: dayjs(),
          payment_mode: 'neft',
          status: 'completed',
        }}
      >
        <Form.Item
          name="payment_date"
          label="Payment Date"
          rules={[{ required: true, message: 'Payment date is required' }]}
        >
          <DatePicker
            style={{ width: '100%' }}
            format="DD MMM YYYY"
            disabledDate={(d) => d && d.isAfter(dayjs(), 'day')}
          />
        </Form.Item>

        <Form.Item
          name="payment_mode"
          label="Payment Mode"
          rules={[{ required: true, message: 'Payment mode is required' }]}
        >
          <Select onChange={setSelectedMode}>
            {PAYMENT_MODES.map((m) => (
              <Option key={m.value} value={m.value}>
                {m.label}
              </Option>
            ))}
          </Select>
        </Form.Item>

        {needsRef && (
          <Form.Item
            name="reference_number"
            label={
              <Space size={4}>
                Reference Number
                {modeConfig?.hint && (
                  <span style={{ color: '#8c8c8c', fontSize: 12 }}>({modeConfig.hint})</span>
                )}
              </Space>
            }
          >
            <Input
              placeholder={modeConfig?.hint ? `Enter ${modeConfig.hint}` : 'Enter reference number'}
              maxLength={200}
            />
          </Form.Item>
        )}

        <Form.Item
          name="amount"
          label={
            <Space size={4}>
              Amount (₹)
              {outstanding > 0 && (
                <Tag icon={<InfoCircleOutlined />} color="blue" style={{ fontSize: 11 }}>
                  Outstanding: {fmtMoney(outstanding)}
                </Tag>
              )}
            </Space>
          }
          rules={[
            { required: true, message: 'Amount is required' },
            { type: 'number', min: 0.01, message: 'Amount must be greater than 0' },
            {
              validator: (_, value) => {
                if (value && outstanding > 0 && value > outstanding + 0.01) {
                  return Promise.reject(
                    `Amount cannot exceed outstanding ₹${outstanding.toFixed(2)}`
                  );
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <InputNumber
            style={{ width: '100%' }}
            min={0.01}
            max={outstanding > 0 ? outstanding + 0.01 : undefined}
            precision={2}
            prefix="₹"
            placeholder="0.00"
          />
        </Form.Item>

        <Form.Item name="status" label="Payment Status">
          <Select>
            <Option value="completed">Completed</Option>
            <Option value="pending">Pending (scheduled)</Option>
            <Option value="failed">Failed</Option>
          </Select>
        </Form.Item>

        <Form.Item name="remarks" label="Remarks">
          <Input.TextArea
            rows={2}
            placeholder="Optional note (e.g. First instalment)"
            maxLength={500}
            showCount
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default RecordPaymentModal;
